<script>
  import { parseISO, format } from 'date-fns';
  import CustomDropdown from "./components/Dropdown.svelte";
  import CustomSearchInput from "./components/SearchInput.svelte";

  export let instanceId;
  export let dataJsonPath;
  export let includedTypes;
  export let limit = 20;

  let data = [];
  let loading = true;
  let error = null;
  let years = [];
  let selectedYear = "";
  let searchKeyword = "";
  let uniqueTypes = [];
  let filteredItems = []; // ✅ defined up front

  const isDevelopment = window.location.hostname === "localhost";
  let url = dataJsonPath ?? "https://nmresources.com.au/ASX-NMR-announcements-simplified.json";
  if (isDevelopment) {
    url = "ASX-NMR-announcements-simplified.json";
  }

  async function fetchData() {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch data");

      data = await response.json();

      years = [
        ...new Set(data.data?.items.map((item) => item.date.split("-")[0])),
      ];

      const allTypes = data.data?.items
        ?.flatMap((item) => item.announcementTypes || [])
        .filter(Boolean);
      uniqueTypes = [...new Set(allTypes)];
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  fetchData();

  const handleSearchKeywordUpdate = (event) => {
    searchKeyword = event.detail.value;
  };

  const handleDropdownChange = (value) => {
    selectedYear = value;
    limit = searchKeyword == "" ? 20 : -1;
  };

  const handleClick = (event) => {
    selectedYear = event.target.innerText;
    limit = searchKeyword == "" ? 20 : -1;
  };

  $: if (years.length > 0 && !selectedYear) {
    selectedYear = years[0];
  }

  const getYearFromDate = (dateString) => {
    return new Date(dateString).getFullYear();
  };

  // ✅ Reactive: filtered items
  $: filteredItems = data.data?.items?.filter((item) => {
    if (!item?.date) return false;

    const matchedYear = getYearFromDate(item.date) === parseInt(selectedYear);
    const matchedIncludeTypes = includedTypes?.length > 0
      ? item.announcementTypes.some((type) => includedTypes.includes(type))
      : true;

    const searchKeywordLowerCase = searchKeyword.trim().toLowerCase();
    const matchingSearchText = searchKeyword
      ? item.announcementTypes.some((type) => type.trim().toLowerCase().includes(searchKeywordLowerCase)) ||
        item.headline.trim().toLowerCase().includes(searchKeywordLowerCase)
      : true;

    return matchedYear && matchedIncludeTypes && matchingSearchText;
  }) ?? [];

  // ✅ Group by month
  $: groupedItemsByMonth = (() => {
    const groups = new Map();
    const slicedItems = filteredItems.slice(0, limit === -1 ? filteredItems.length : limit);
    for (const item of slicedItems) {
      if (!item || !item.date) continue;
      const date = parseISO(item.date);
      const monthKey = format(date, "yyyy-MM");
      if (!groups.has(monthKey)) {
        groups.set(monthKey, []);
      }
      groups.get(monthKey).push(item);
    }

    // Optional: sort by newest month first
    return new Map([...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)));
  })();

  // Format date for display
  function formatDateToSydney(dateStr) {
    const date = new Date(dateStr);
    const options = {
      timeZone: "Australia/Sydney",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-AU", options).format(date);
  }
</script>

<main class="asx-announcements-tab">
  {#if loading}
    <p>Loading data...</p>
  {:else if error}
    <p>Error: {error}</p>
  {:else}
    <div class="asx-announcements-tab__nav">
      <ul>
        {#each years.slice(0, 4) as year}
          <li class={selectedYear == year ? "active" : ""}>
            <button on:click={handleClick} title="ASX announcements in {year}"
              >{year}</button
            >
          </li>
        {/each}
      </ul>
      <div>
        <CustomSearchInput
          on:updateSearchKeyword={handleSearchKeywordUpdate}
          placeholder="Search keywords"
        />
        <CustomDropdown
          options={years}
          bind:selectedOption={selectedYear}
          onOptionSelect={handleDropdownChange}
        />
      </div>
    </div>
    <div class="asx-announcements-tab__body">
      <p class="asx-announcements-tab__results">
        {#if filteredItems.length > 0}
          <strong>{filteredItems.length}</strong>
          {searchKeyword !== "" ? "matching " : "total "}
          {filteredItems.length > 1 ? "records " : "record "} in {selectedYear}.
        {:else}
          No matching results.
        {/if}
      </p>
      <ul>
        {#each Array.from(groupedItemsByMonth.entries()) as [monthKey, items]}
          {#if items[0]?.date}
            <li class="asx-announcements-tab__month-heading">
              <h3>{format(parseISO(items[0].date), "MMMM yyyy")}</h3>
            </li>
          {/if}
      
          {#each items as item}
            <li class="asx-announcements-tab__list">
              <div class="asx-announcements-tab__item">
                <div class="asx-announcements-tab__icon">
                  <svg
                    class="svg-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 83.67 83.67"
                  >
                    <polyline
                      fill="none"
                      stroke="currentColor"
                      stroke-miterlimit="10"
                      stroke-width="8"
                      points="35.18 24.29 52.8 41.91 35.18 59.52"
                    />
                    <rect fill="none" width="83.67" height="83.67" />
                  </svg>
                </div>
                <div>
                  <p>
                    <a
                      href="https://cdn-api.markitdigital.com/apiman-gateway/ASX/asx-research/1.0/file/{item.documentKey}"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.headline}
                      <i class="icon icomoon-fontawesome-16x16-external-link-alt"></i>
                    </a>
                  </p>
                  <p>
                    {#if item.announcementTypes?.length > 0}
                      {#each item.announcementTypes as type}
                        <span class="asx-announcements-tab__type">{type}</span>
                      {/each}
                    {/if}
                  </p>
                  <p><time>{formatDateToSydney(item.date)}</time></p>
                </div>
              </div>
            </li>
          {/each}
        {/each}
      </ul>
      {#if limit > -1 && searchKeyword == "" && filteredItems.length > limit}
        <button
          class="default-btn-shortcode dt-btn dt-btn-s link-hover-off"
          on:click={() => {
            limit = -1;
          }}>SHOW ALL</button
        >
      {/if}
    </div>
  {/if}
</main>

<style>
  main.asx-announcements-tab {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .asx-announcements-tab__nav {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-grow: 1;
    border-bottom: 1px solid #ebebeb;
  }

  @media screen and (max-width: 768px) {
    .asx-announcements-tab__nav {
      flex-direction: column;
    }
  }

  .asx-announcements-tab .asx-announcements-tab__nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
  }
  @media screen and (max-width: 768px) {
    .asx-announcements-tab__nav ul {
      order: 2;
    }
    .asx-announcements-tab__results {
      order: 1;
    }
  }
  .asx-announcements-tab .asx-announcements-tab__nav li {
    margin: 0;
  }
  .asx-announcements-tab .asx-announcements-tab__nav button {
    border: none;
    padding: 15px;
    margin: 0;
    font-size: 16px;
    background: none;
    cursor: pointer;
    color: #333;
  }
  .asx-announcements-tab .asx-announcements-tab__nav button:hover {
    background: #ebebeb;
  }
  .asx-announcements-tab .asx-announcements-tab__nav li.active button {
    background: #aa5526;
    font-weight: bold;
    color: #fff;
  }
  .asx-announcements-tab__results {
    text-align: center;
    margin: 20px 0 0;
  }
  .asx-announcements-tab__month-heading h3 {
    margin-top: 16px;
    font-size: 18px;
    font-weight: bold;
  }
  @media screen and (max-width: 768px) {
    .asx-announcements-tab__results {
      text-align: center;
    }
  }
  .asx-announcements-tab__body ul {
    list-style: none;
    padding: 0.5rem 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    border: 0px solid #ccc;
  }

  .asx-announcements-tab__body li {
    width: 100%;
  }
  .asx-announcements-tab__body li.asx-announcements-tab__list:hover {
    background: rgba(170, 85, 58, 0.1);
  }

  .asx-announcements-tab__item {
    padding: 0.5rem;
    display: flex;
    align-items: flex-start;
  }
  .asx-announcements-tab__item p {
    padding: 0;
    margin: 0;
    font-size: 15px;
    line-height: 1.25;
  }
  .asx-announcements-tab__item a {
    text-decoration: none;
  }
  .asx-announcements-tab__item a:hover {
    text-decoration: underline;
  }
  .asx-announcements-tab__item p i.icon {
    margin-left: 0.25rem;
    font-size: 12px;
  }
  .asx-announcements-tab__item p time {
    color: #999;
    font-size: 13px;
  }
  .asx-announcements-tab__icon {
    padding-right: 0.5rem;
  }
  .svg-icon {
    width: 1rem; /* Adjust size as needed */
    height: 1rem; /* Adjust size as needed */
    fill: currentColor; /* This will use the current text color */
    background: #aa5526;
    border-radius: 50%;
    color: #fff;
  }
  .asx-announcements-tab__type {
    font-size: 13px;
    display: inline-block;
    border-right: 1px solid #ebebeb;
    padding: 0 0.5rem;
    color: #333333;
  }
  .asx-announcements-tab__type:first-child {
    padding-left: 0;
  }
  .asx-announcements-tab__type:last-child {
    padding-right: 0;
    border-right: none;
  }
</style>
